/**
 * URL and link handling.
 *
 * `internal` and `external` describe only how a link was authored (Contentful
 * entity vs URL literal). Tab target and navigation mode are separate
 * questions, answered by `classifyLink` from the resolved host and path.
 *
 * Read `docs/agents/url-and-links.md` before changing the domain lists or the
 * classification rules.
 *
 * The static host/path lists live here rather than in `@sola/config` because
 * config already depends on this lib. They do not need env access: host
 * comparison is done on the environment-normalized form, so the production
 * lists match in every environment.
 *
 * File order matters: definitions must precede use (no-use-before-define).
 */

// ---------------------------------------------------------------------------
// Shape predicates — no domain knowledge, no ordering dependencies.
// ---------------------------------------------------------------------------

/** Upgrades http:// to https://. We never link over plain http. */
export const toHttps = (link: string): string =>
  link.trim().replace(/^http:\/\//i, 'https://');

export const stripEdgeSlashes = (value: string): string =>
  // eslint-disable-next-line sonarjs/slow-regex
  value.trim().replaceAll(/^\/+|\/+$/g, '');

/**
 * Checks if a path is purely numeric after trimming whitespace and
 * stripping leading/trailing slashes.
 *
 * @param path - The URL path to check (e.g., "/2024", "123456", "/00123/")
 * @returns true if the cleaned path is composed entirely of digits
 */
export const isNumericPath = (path: string): boolean => {
  const cleaned = stripEdgeSlashes(path);
  if (!cleaned) {
    return false;
  }
  return /^\d+$/.test(cleaned);
};

export const isAnchorLink = (link: string): boolean => {
  if (!link || !link.trim()) {
    return false;
  }
  const trimmedLink = link.trim();
  return trimmedLink.startsWith('#');
};

export const isMailToLink = (link: string): boolean => {
  if (!link || !link.trim()) {
    return false;
  }
  const trimmedLink = link.trim();
  return (
    trimmedLink.toLowerCase().startsWith('mailto:') ||
    /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(trimmedLink)
  );
};

// Helper function to validate phone numbers more strictly
const isValidPhoneNumber = (phoneNumber: string): boolean => {
  if (!phoneNumber || !phoneNumber.trim()) {
    return false;
  }

  const trimmed = phoneNumber.trim();

  // Check for invalid characters that suggest it's not a phone number.
  // Allow digits, spaces, hyphens, dots, parentheses, and +. A + is normally a
  // leading country-code prefix, but authors also type it mid-string (e.g.
  // "1+800-336-7676"); non-leading + chars are stripped below rather than
  // rejected here.
  const validCharsPattern = /^[()\d\s.+-]+$/;
  if (!validCharsPattern.test(trimmed)) {
    return false;
  }

  // Check for patterns that suggest it's not a phone number
  // Reject if it contains letters (except in tel: prefix which is already handled)
  if (/[a-zA-Z]/.test(trimmed)) {
    return false;
  }

  // Reject if it contains @ (email-like)
  if (trimmed.includes('@')) {
    return false;
  }

  // Reject if it looks like a URL
  if (trimmed.includes('://') || trimmed.includes('www.')) {
    return false;
  }

  // Reject if it has extensions or additional text
  if (/x\d+$|ext\d+$|extension\d+$/i.test(trimmed)) {
    return false;
  }

  // Reduce to digits, preserving only a leading + (country-code prefix).
  // Strip any other + so mid-string separators like "1+800-336-7676" normalize
  // to a plain digit string.
  const leadingPlus = trimmed.startsWith('+') ? '+' : '';
  const digitsOnly = leadingPlus + trimmed.replaceAll(/\D/g, '');

  // Must be at least 10 digits long (excluding +)
  const phoneDigits = digitsOnly.replace(/^\+/, '');
  if (phoneDigits.length < 10 || phoneDigits.length > 15) {
    return false;
  }

  const phonePatterns = [
    /^[+]?1?[2-9]\d{2}[2-9]\d{2}\d{4}$/, // US format: +1234567890, 1234567890, 2345678901
    /^[+]?\d{10,15}$/, // International format: 10-15 digits
  ];

  // Check if the digits match a valid phone pattern
  const digitsMatch = phonePatterns.some((pattern) => pattern.test(digitsOnly));

  // Also check formatted US numbers, but be more strict
  const formattedUSPattern =
    /^[+]?1?[-.\s]?[(]?\d{3}[)]?[-.\s]?\d{3}[-.\s]?\d{4}$/;
  const formattedMatch = formattedUSPattern.test(trimmed);

  return digitsMatch || formattedMatch;
};

/**
 * Splits a link into its `tel:` scheme (if present) and the number that
 * follows, trimming surrounding whitespace. This is the single place that
 * knows how to detect and slice the scheme, so `isTelLink`, `isTelShaped`, and
 * `normalizeTelValue` stay consistent about case, trimming, and the slice
 * boundary.
 *
 * `scheme` preserves the author's original case (`tel:` / `TEL:` / `Tel:`);
 * `number` is the trimmed remainder. For a schemeless value `scheme` is `''`
 * and `number` is the whole trimmed input.
 */
const splitTel = (link: string): { scheme: string; number: string } => {
  const trimmed = link.trim();
  if (trimmed.toLowerCase().startsWith('tel:')) {
    return { scheme: trimmed.slice(0, 4), number: trimmed.slice(4).trim() };
  }
  return { scheme: '', number: trimmed };
};

export const isTelLink = (link: string): boolean => {
  if (!link || !link.trim()) {
    return false;
  }

  // `number` is the post-scheme remainder, or the whole trimmed value when
  // there is no scheme — either way it is what gets validated.
  return isValidPhoneNumber(splitTel(link).number);
};

/**
 * A looser sibling of `isTelLink` for the "should this pass through untouched?"
 * decision, not the "is this a valid phone number?" decision.
 *
 * `isTelLink` demands a well-formed number (length, US/international patterns),
 * which is right for a form field that can surface an error. Some callers have
 * no error path and must instead choose between two side effects: preserve the
 * author's `tel:` string as-is, or fall through to URL normalization — which
 * would prepend the site root and corrupt it into `https://.../tel:...`.
 *
 * For that decision the question is only "does this plausibly resemble a phone
 * number?": it must carry a `tel:` scheme and contain at least one digit.
 * Letters are allowed, so vanity numbers (e.g. `tel:1-800-FLOWERS`) and
 * borderline-but-real numbers whose strict validation fails (e.g.
 * `tel:1+800-336-7676`) stay on the pass-through path, while a value with no
 * digits at all (e.g. `tel:sadosdfg`) is rejected so it can be handled
 * normally rather than silently preserved.
 */
export const isTelShaped = (link: string): boolean => {
  if (!link || !link.trim()) {
    return false;
  }

  const { scheme, number } = splitTel(link);
  if (!scheme || !number) {
    return false;
  }

  // Require at least one digit so an all-punctuation value is not treated as a
  // number. Letters are allowed so vanity numbers (e.g. `tel:1-800-FLOWERS`)
  // stay on the pass-through path.
  return /\d/.test(number);
};

/**
 * Normalizes a tel value for storage: removes non-leading `+` while preserving
 * the `tel:` scheme and a single leading `+` country-code prefix.
 *
 * Authors type `+` mid-string as a separator (e.g. `1+800-336-7676`).
 * `isValidPhoneNumber` already ignores those when it counts digits, but it
 * never rewrites the value, so the stray `+` survives into what gets stored.
 * This produces the cleaned form to store instead — the pluses are simply
 * deleted, not replaced, and the rest of the formatting (spaces, hyphens,
 * parentheses) is left untouched so the number still passes validation.
 *
 *   tel:1+800-336-7676 -> tel:1800-336-7676
 *   +1+800+555+0199     -> +18005550199
 *   1+800+555+0199      -> 18005550199
 *
 * Scheme detection, case, and whitespace trimming are shared with the tel
 * predicates via `splitTel`, so the leading `+` is detected on the trimmed
 * number (e.g. `tel: +1 800` keeps its `+`). A leading `+` is kept; every
 * other `+` in the number is removed.
 */
export const normalizeTelValue = (link: string): string => {
  if (!link) {
    return link;
  }

  const { scheme, number } = splitTel(link);

  const leadingPlus = number.startsWith('+') ? '+' : '';
  const rest = leadingPlus ? number.slice(1) : number;

  return `${scheme}${leadingPlus}${rest.replaceAll('+', '')}`;
};

// ---------------------------------------------------------------------------
// Path lists and path matching.
// ---------------------------------------------------------------------------

/**
 * Paths on dotcom still served by the legacy site. These need a full page load
 * so the Cloudflare www-router can intercept and fall back to legacy when
 * Skippy returns a 404. They are same-domain, so they open in the same tab.
 *
 * As pages migrate, remove them from this list.
 *
 * Paths ending in `.htm`/`.html` do NOT need an entry here — they are matched
 * by suffix (see `hasLegacyExtension`), since a migrated page gets a new
 * extensionless slug.
 */
export const legacyDotcomPaths = [
  '/account',
  '/my-account',
  '/mobile-app/connect-with-your-sponsored-child',
  '/sponsor_a_child/select-a-child-for-me',
  '/untojesus',
];

/**
 * Reduces a path to a comparable form: lowercased, no query, no hash, and
 * exactly one leading slash with no trailing slash. Skippy and My run with
 * `trailingSlash: true` but legacy URLs are inconsistent, so every path
 * comparison has to be slash-insensitive.
 */
const canonicalizePath = (path: string): string => {
  const withoutFragment = path.split('#')[0].split('?')[0];
  return `/${stripEdgeSlashes(withoutFragment.toLowerCase())}`;
};

/**
 * True when `path` equals or sits underneath one of `entries`. Compares on
 * segment boundaries, so `/accountability` does NOT match `/account`.
 */
const matchesPathEntry = (path: string, entries: string[]): boolean => {
  const canonical = canonicalizePath(path);
  return entries.some((entry) => {
    const canonicalEntry = canonicalizePath(entry);
    return (
      canonical === canonicalEntry || canonical.startsWith(`${canonicalEntry}/`)
    );
  });
};

/**
 * Legacy pages are extensionless only after migration, so treat .htm/.html as
 * legacy. Suffix-matched on the path: a query string containing ".htm" must not
 * drag a modern page into a full page load.
 */
const hasLegacyExtension = (path: string): boolean =>
  /\.html?$/.test(canonicalizePath(path));

// ---------------------------------------------------------------------------
// Host model.
// ---------------------------------------------------------------------------

/**
 * Collapses environment-specific host variants onto their production form, so
 * one static host list works in every environment.
 *
 *   www.stage.compassion.com  -> www.compassion.com
 *   stage.compassion.com      -> compassion.com
 *   write-test.compassion.com -> write.compassion.com
 *
 * Preserves the app subdomain: `www.`, `my.`, `auth.` and `checkout.` must stay
 * distinguishable, since each is a different app.
 */
const stripEnvPrefix = (host: string): string =>
  host
    .toLowerCase()
    // Dotted env segment: www.stage.compassion.com -> www.compassion.com
    .replace(/^([a-z0-9-]+)\.(stage|devint|local)\./, '$1.')
    // Bare env host: stage.compassion.com -> compassion.com
    .replace(/^(stage|devint|local)\./, '')
    // Hyphenated env suffix: write-test.compassion.com -> write.compassion.com.
    // This naming scheme is specific to the Letter Writing app.
    .replace(/^write-(test|stage)\./, 'write.');

/**
 * Bare apexes we own that canonically serve from `www.`. A raw apex 301s to its
 * www host, so we rewrite it before anything else looks at it: authors never
 * store an apex link, and classification treats it as the www site rather than
 * as a separate host.
 */
const wwwCanonicalApexes = ['compassion.com', 'compassionexperience.com'];

/**
 * Rewrites a bare owned apex to its `www.` form, preserving any environment
 * segment.
 *
 *   compassion.com        -> www.compassion.com
 *   stage.compassion.com  -> www.stage.compassion.com
 *   my.compassion.com     -> unchanged (not an apex)
 *   example.com           -> unchanged (not ours)
 */
const canonicalizeApexHost = (host: string): string => {
  const lower = host.toLowerCase();
  return wwwCanonicalApexes.includes(stripEnvPrefix(lower))
    ? `www.${lower}`
    : lower;
};

/**
 * Exact hostnames of apps we own and link to, in production form.
 *
 * Exact hosts only — never apex/suffix matching. An unrecognized subdomain on
 * our apex is not one of our apps and opens in a new tab.
 *
 * Environment variants do not need entries: `stripEnvPrefix` folds
 * `www.devint.compassion.com` and `write-test.compassion.com` onto their
 * production form before matching.
 *
 * Bare apexes are listed alongside their `www.` form. An apex typically 301s to
 * `www.`, so it classifies as `sola-app` (full page load, same tab) rather than
 * a client route — which is correct, since the redirect crosses origins.
 */
export const solaDomains = [
  'compassion.com',
  'www.compassion.com',
  'my.compassion.com',
  'auth.compassion.com',
  'cart.compassion.com',
  'checkout.compassion.com',
  'write.compassion.com',
  'compassionexperience.com',
  'www.compassionexperience.com',
];

/**
 * Hosts we own that must never be a link or redirect target. Authoring one is a
 * mistake, not a navigation choice.
 *
 * - `skippy.` is an internal origin behind the CDN; always use `www.` instead
 * - `embeds.` is iframe-only
 * - `blog.` and `compassionsunday.com` are redirect-only frontends
 */
export const neverLinkDomains = [
  'skippy.compassion.com',
  'embeds.compassion.com',
  'blog.compassion.com',
  'www.compassionsunday.com',
  'compassionsunday.com',
];

/**
 * Domains whose pages live in the same Contentful space, so a URL on any of
 * them could have been authored as an internal (entry) link instead.
 *
 * Skippy and My share a space and cross-link. Compex has its own space and can
 * only reference itself. Kept in sync with `contentfulSpace` in the
 * `libs/multisite` site definitions.
 */
export const contentLinkDomainGroups = [
  ['compassion.com', 'www.compassion.com', 'my.compassion.com'],
  ['compassionexperience.com', 'www.compassionexperience.com'],
];

/**
 * Parses `link` as an absolute https URL, or returns null when it is relative,
 * a non-http scheme (mailto:, tel:), or unparseable.
 *
 * Authors regularly paste protocol-less URLs ("www.compassion.com"), so those
 * are accepted and assumed https.
 */
const parseHttpsUrl = (link: string): URL | null => {
  const trimmed = toHttps((link || '').trim());
  if (!trimmed) {
    return null;
  }

  // Protocol-relative "//host" is a real destination the browser will follow,
  // so resolve it rather than treating it as a path. Classifying it by host is
  // what earns it a new tab and rel="noopener".
  if (trimmed.startsWith('//')) {
    try {
      return new URL(`https:${trimmed}`);
    } catch {
      return null;
    }
  }

  if (trimmed.startsWith('/') || trimmed.startsWith('#')) {
    return null;
  }

  const hasScheme = /^[a-z][a-z\d+.-]*:/i.test(trimmed);
  if (hasScheme && !/^https:\/\//i.test(trimmed)) {
    return null;
  }

  try {
    return new URL(hasScheme ? trimmed : `https://${trimmed}`);
  } catch {
    return null;
  }
};

/**
 * As `parseHttpsUrl`, with a bare owned apex rewritten to its `www.` host, so
 * every host comparison downstream sees the canonical form.
 */
const toAbsoluteHttpsUrl = (link: string): URL | null => {
  const parsed = parseHttpsUrl(link);
  if (!parsed) {
    return null;
  }
  parsed.hostname = canonicalizeApexHost(parsed.hostname);
  return parsed;
};

/**
 * The URL that should actually be stored or rendered: https, and never a bare
 * owned apex.
 *
 * Only rewrites when the host genuinely changed — otherwise the author's exact
 * string is preserved, since `URL.href` normalizes paths and adds trailing
 * slashes.
 */
export const canonicalizeUrl = (link: string): string => {
  const trimmed = toHttps((link || '').trim());
  const raw = parseHttpsUrl(trimmed);
  if (!raw) {
    return trimmed;
  }

  const canonicalHost = canonicalizeApexHost(raw.hostname);
  if (canonicalHost === raw.hostname.toLowerCase()) {
    return trimmed;
  }

  raw.hostname = canonicalHost;
  return raw.href;
};

/** Extracts a lowercased hostname, or null for relative paths and unparseable input. */
export const getUrlHost = (link: string): string | null =>
  toAbsoluteHttpsUrl(link)?.hostname.toLowerCase() ?? null;

/**
 * True when `link` points at an exact host in `domains`, comparing on the
 * environment-normalized form. Defaults to the hosts we own.
 */
export const isSolaDomain = (
  link: string,
  domains: string[] = solaDomains,
): boolean => {
  const host = getUrlHost(link);
  if (!host) {
    return false;
  }
  const normalizedHost = stripEnvPrefix(host);
  return domains.some(
    (domain) => stripEnvPrefix(domain.toLowerCase()) === normalizedHost,
  );
};

/** True for hosts we own but must never link to (iframe-only, redirect-only). */
export const isNeverLinkDomain = (link: string): boolean =>
  isSolaDomain(link, neverLinkDomains);

/** True when `link` resolves to a dotcom path still served by legacy. */
export const isLegacyDotcomPath = (link: string): boolean => {
  if (!link || !link.trim()) {
    return false;
  }

  const parsed = toAbsoluteHttpsUrl(link);

  // Absolute URLs qualify only on the canonical dotcom application. Relative
  // paths are interpreted as dotcom because this helper is explicitly scoped
  // to the legacy dotcom surface.
  if (parsed && stripEnvPrefix(parsed.hostname) !== 'www.compassion.com') {
    return false;
  }

  const path = parsed ? parsed.pathname : link;
  return hasLegacyExtension(path) || matchesPathEntry(path, legacyDotcomPaths);
};

/**
 * True when `link` resolves to one of `appRoutePaths` — this app's file-based
 * routes, which have no path-addressed Contentful entry behind them.
 *
 * Pass the current site's list and domain from its `libs/multisite` site
 * definition. Relative paths belong to the current site; absolute URLs must
 * resolve to that same site so one app's route list cannot exempt a sibling.
 */
export const isAppRoutePath = (
  link: string,
  appRoutePaths: string[],
  currentSiteDomain = 'www.compassion.com',
): boolean => {
  if (!link || !link.trim()) {
    return false;
  }

  const parsed = toAbsoluteHttpsUrl(link);
  if (
    parsed &&
    stripEnvPrefix(parsed.hostname) !==
      stripEnvPrefix(canonicalizeApexHost(currentSiteDomain.toLowerCase()))
  ) {
    return false;
  }
  if (!parsed && !link.trim().startsWith('/')) {
    return false;
  }

  return matchesPathEntry(parsed ? parsed.pathname : link, appRoutePaths);
};

// ---------------------------------------------------------------------------
// Classification — the single decision point.
// ---------------------------------------------------------------------------

/** How a resolved URL should be navigated to and which tab it opens in. */
export type LinkKind =
  | 'anchor'
  | 'current-app-path'
  | 'invalid'
  | 'legacy-path'
  | 'mailto'
  | 'never-link'
  | 'sola-app'
  | 'tel'
  | 'third-party';

export type LinkClassification = {
  kind: LinkKind;
  /**
   * 'client-route' may use the Next router. 'full-load' means render a native
   * <a>; for anchors/mailto/tel that does not literally reload the page, it
   * just means "do not hand this to the router".
   */
  navigation: 'client-route' | 'full-load';
  target: '_blank' | '_self';
  /** The https-coerced URL to actually render. */
  url: string;
};

export type ClassifyLinkContext = {
  /**
   * The host whose paths this app can client-route. Relative links are assumed
   * to belong to it.
   *
   * Defaults to dotcom. Omitting it is the conservative choice: an absolute URL
   * on another host we own is then classified `sola-app`, so it gets a full page
   * load instead of a client route. A full load always works, whereas
   * client-routing a URL this app cannot serve does not. Pass it where the site
   * is known — the editor does, so authoring validation is site-accurate.
   */
  currentSiteDomain?: string;
};

/**
 * The single decision point for how to render a link. Prefer this over
 * composing the individual predicates, so every consumer agrees.
 */
export const classifyLink = (
  href: string,
  context: ClassifyLinkContext = {},
): LinkClassification => {
  const { currentSiteDomain = 'www.compassion.com' } = context;

  const trimmed = (href || '').trim();
  if (!trimmed) {
    return {
      kind: 'invalid',
      navigation: 'full-load',
      target: '_self',
      url: trimmed,
    };
  }

  // Anchors, mailto and tel all render as a native <a>.
  if (isAnchorLink(trimmed)) {
    return {
      kind: 'anchor',
      navigation: 'full-load',
      target: '_self',
      url: trimmed,
    };
  }
  if (isMailToLink(trimmed)) {
    return {
      kind: 'mailto',
      navigation: 'full-load',
      target: '_self',
      url: trimmed,
    };
  }
  if (isTelLink(trimmed)) {
    return {
      kind: 'tel',
      navigation: 'full-load',
      target: '_self',
      url: trimmed,
    };
  }

  // Normalize protocol-relative input to an explicit https URL so the rendered
  // href cannot silently inherit a different scheme, and rewrite a bare owned
  // apex to its www. host so we never emit an apex link.
  const url = canonicalizeUrl(
    trimmed.startsWith('//') ? `https:${trimmed}` : toHttps(trimmed),
  );

  // Legacy paths are a dotcom concern. Other sites were never on the legacy
  // platform, so `/account` there is an ordinary path and must not be forced
  // into a full page load.
  const isDotcom =
    canonicalizeApexHost(stripEnvPrefix(currentSiteDomain.toLowerCase())) ===
    'www.compassion.com';

  const classifyPath = (path: string): LinkClassification =>
    isDotcom &&
    (matchesPathEntry(path, legacyDotcomPaths) || hasLegacyExtension(path))
      ? { kind: 'legacy-path', navigation: 'full-load', target: '_self', url }
      : {
          kind: 'current-app-path',
          navigation: 'client-route',
          target: '_self',
          url,
        };

  // Relative paths belong to the current app. "//host" is not relative.
  if (url.startsWith('/') && !url.startsWith('//')) {
    return classifyPath(url);
  }

  const parsed = toAbsoluteHttpsUrl(url);
  if (!parsed) {
    return { kind: 'invalid', navigation: 'full-load', target: '_self', url };
  }

  if (isNeverLinkDomain(url)) {
    return {
      kind: 'never-link',
      navigation: 'full-load',
      target: '_self',
      url,
    };
  }

  const isCurrentSite =
    stripEnvPrefix(parsed.hostname) ===
    stripEnvPrefix(canonicalizeApexHost(currentSiteDomain.toLowerCase()));
  if (isCurrentSite) {
    return classifyPath(parsed.pathname);
  }

  // A different app we own: full page load, but still the same tab.
  if (isSolaDomain(url)) {
    return { kind: 'sola-app', navigation: 'full-load', target: '_self', url };
  }

  return {
    kind: 'third-party',
    navigation: 'full-load',
    target: '_blank',
    url,
  };
};

/**
 * True when `link` is a path this app can route to itself — a relative path, or
 * an absolute URL on the current site that is not legacy-served.
 *
 * For "should this have been an entry reference?" prefer `isContentLinkableUrl`,
 * which also covers sibling apps in the same Contentful space.
 */
export const isCurrentSitePath = (
  link: string,
  currentSiteDomain?: string,
): boolean =>
  classifyLink(link, { currentSiteDomain }).kind === 'current-app-path';

/**
 * True when `link` points at a page that could have been authored as an internal
 * (entry) link — either on this site, or on a sibling app sharing this site's
 * Contentful space.
 *
 * Use this to steer authors toward internal links. Broader than
 * `isCurrentSitePath`: from Skippy, a `my.compassion.com` page is internally
 * linkable even though it is a different app. Legacy paths are excluded, since
 * they have no entry to reference.
 */
export const isContentLinkableUrl = (
  link: string,
  currentSiteDomain = 'www.compassion.com',
): boolean => {
  const { kind } = classifyLink(link, { currentSiteDomain });
  if (kind === 'current-app-path') {
    return true;
  }
  if (kind !== 'sola-app' || isLegacyDotcomPath(link)) {
    return false;
  }

  const host = getUrlHost(link);
  if (!host) {
    return false;
  }

  const normalizedHost = stripEnvPrefix(host);
  const normalizedSite = stripEnvPrefix(currentSiteDomain.toLowerCase());
  return contentLinkDomainGroups.some((group) => {
    const normalizedGroup = new Set(
      group.map((domain) => stripEnvPrefix(domain.toLowerCase())),
    );
    return (
      normalizedGroup.has(normalizedSite) && normalizedGroup.has(normalizedHost)
    );
  });
};

/**
 * Validates a URL an author typed into an "external link" field: a well-formed
 * absolute URL that is not a page on the current site.
 *
 * A URL literal pointing at another app we own (auth, checkout, write) is valid.
 * `http://` is coerced to https rather than rejected.
 */
export const isValidOffSiteUrl = (
  link: string,
  currentSiteDomain?: string,
): boolean => {
  if (!link || !link.trim()) {
    return false;
  }
  const coerced = toHttps(link);

  // @TODO: TikTok urls contain "@", we'll need to account for this

  if (
    coerced.match(
      /^https:\/\/[a-zA-Z0-9-]+([-.]{1}[a-zA-Z0-9-]+)*\.[a-zA-Z]{2,}/,
    ) === null ||
    coerced.match(/^(?![^/]*@).*$/) === null
  ) {
    return false;
  }

  if (isAnchorLink(coerced) || isMailToLink(coerced) || isTelLink(coerced)) {
    return false;
  }

  const { kind } = classifyLink(coerced, { currentSiteDomain });

  // Only successfully parsed URL destinations outside the current app are
  // authorable here. Invalid, current-app, and never-link targets are rejected.
  return ['legacy-path', 'sola-app', 'third-party'].includes(kind);
};

// ---------------------------------------------------------------------------
// Redirect safety and generic URL helpers.
// ---------------------------------------------------------------------------

/**
 * Suffix-matching host check: `example.com` also matches `sub.example.com`.
 *
 * Suffix matching is intentional. We control DNS for our own apex, so any
 * `*.compassion.com` record points somewhere we chose, even when vendor-hosted.
 *
 * Do NOT use this to decide tab target — trusted and "one of our apps" are
 * different questions. Use `isSolaDomain` (exact hosts) for that.
 */
export const isUrlFromTrustedDomain = (
  url: string,
  domains: string[],
): boolean => {
  try {
    const urlObj = new URL(url);
    return domains.some(
      (domain) =>
        urlObj.hostname === domain || urlObj.hostname.endsWith(`.${domain}`),
    );
  } catch {
    return false;
  }
};

/** Checks an HTTP referrer against the current origin and an explicit set of internal origins. */
export const isExternalReferrer = (
  referrer: string | null,
  currentOrigin: string,
  internalOrigins: ReadonlySet<string>,
): boolean => {
  if (!referrer) {
    return false;
  }

  try {
    const { origin, protocol } = new URL(referrer);
    return (
      (protocol === 'https:' || protocol === 'http:') &&
      origin !== currentOrigin &&
      !internalOrigins.has(origin)
    );
  } catch {
    return false;
  }
};

export const validateUrl = (input: string) => {
  if (!URL.canParse(input) || !input?.startsWith('https://')) {
    return null;
  }

  return new URL(input);
};

export const parseNullableUrl = (input?: string | null) => {
  if (!input) {
    return null;
  }

  const trimmedInput = input.trim();

  if (!trimmedInput) {
    return null;
  }

  try {
    const url = new URL(trimmedInput);

    return url.protocol === 'https:' ? url.toString() : null;
  } catch {
    return null;
  }
};

export const slashes = (
  str: string | undefined,
  pattern: 'both' | 'none' | 'prefix-only' | 'suffix-only',
) => {
  const safeStr = str || '';
  const noSlashesString = safeStr.split('/').filter(Boolean).join('/');

  // Root path "/" strips to empty string — return "/" for any slash pattern
  if (!noSlashesString) {
    return pattern === 'none' ? '' : '/';
  }

  if (pattern === 'both') {
    return `/${noSlashesString}/`;
  }
  if (pattern === 'prefix-only') {
    return `/${noSlashesString}`;
  }
  if (pattern === 'suffix-only') {
    return `${noSlashesString}/`;
  }

  return noSlashesString;
};

export const convertObjToSearchParams = <T>(input?: T) => {
  const params = new URLSearchParams();
  if (!input) {
    return params;
  }

  for (const [key, value] of Object.entries(input)) {
    if (value === undefined || value === null) {
      continue;
    }
    if (Array.isArray(value)) {
      for (const v of value) {
        params.append(key, String(v));
      }
    } else {
      params.append(key, String(value));
    }
  }
  return params;
};

// ---------------------------------------------------------------------------
// Campaign redirect allowlist — NOT part of the link classification model.
//
// @TODO Revisit whether this is needed at all. The domain list looks arbitrary,
// and it is not clear these campaign redirects should happen in the first place.
// Left as-is deliberately; investigate and remove or replace separately rather
// than folding it into the link/redirect terminology work.
// ---------------------------------------------------------------------------

/** Domains considered safe for redirect targets. */
const REDIRECT_ALLOWED_DOMAINS = [
  'compassion.com',
  'compassionsunday.com',
  'compassion.ca',
];

/**
 * Returns true when `url` is safe to redirect to: either a relative path
 * (starts with "/" but not "//") or an absolute URL on one of our allowed
 * domains.
 */
export const isSafeRedirectUrl = (url: string): boolean => {
  const trimmed = url.trim();
  if (!trimmed) {
    return false;
  }

  // Reject backslashes — browsers normalize "\" to "/" which can bypass
  // protocol-relative checks (e.g. "\evil.com" becomes "//evil.com").
  if (trimmed.includes('\\')) {
    return false;
  }

  // Relative paths are safe (but reject protocol-relative "//evil.com")
  if (trimmed.startsWith('/') && !trimmed.startsWith('//')) {
    return true;
  }

  return isUrlFromTrustedDomain(trimmed, REDIRECT_ALLOWED_DOMAINS);
};
